import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineItemsEditor, linesValid } from "@/components/stock/LineItemsEditor";
import { FormField, WarehouseSelect } from "@/components/stock/FormField";
import { toast } from "sonner";

const LINE_EDITABLE = ["manual", "used"];

// Edit a stock document: items for Masuk/Keluar/Pemakaian, header fields for the rest.
export const DocEditDialog = ({ tx, onClose, items, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  useEffect(() => {
    setForm(null);
    if (!tx) return;
    api.get("/stock-transactions", { params: { doc_no: tx.doc_no } }).then(({ data }) => setForm({
      date: tx.date, ref_no: tx.ref_no || "", note: tx.note || "", warehouse: tx.warehouse,
      lines: data.map((l) => ({ item_type: l.item_type, item_id: l.item_id, qty: l.qty })),
    }));
  }, [tx]);
  if (!tx) return null;
  const full = LINE_EDITABLE.includes(tx.ref_type);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (full && !linesValid(form.lines)) return toast.error(t("lines_invalid"));
    const body = { date: form.date, ref_no: form.ref_no, note: form.note };
    if (full) Object.assign(body, { warehouse: form.warehouse, lines: form.lines.map((l) => ({ item_type: l.item_type, item_id: l.item_id, qty: Number(l.qty) })) });
    try {
      await api.put(`/stock-docs/${tx.doc_no}`, body);
      toast.success(`${t("saved")} · ${tx.doc_no}`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="doc-edit-dialog">
        <DialogHeader><DialogTitle>{t("edit_doc")} · <span className="font-mono">{tx.doc_no}</span> · {t(`src_${tx.ref_type}`)}</DialogTitle></DialogHeader>
        {!form ? <div className="py-6 text-center text-slate-400">…</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <FormField label={t("date")}><Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} data-testid="doc-edit-date" /></FormField>
              {full && (
                <FormField label={t("warehouse")}>
                  <WarehouseSelect value={form.warehouse} onChange={set("warehouse")} availableOnly={tx.type === "in"} testid="doc-edit-warehouse" />
                </FormField>
              )}
              <FormField label={t("ref_no")}><Input value={form.ref_no} onChange={(e) => set("ref_no")(e.target.value)} data-testid="doc-edit-ref" /></FormField>
              <FormField label={t("note")}><Input value={form.note} onChange={(e) => set("note")(e.target.value)} data-testid="doc-edit-note" /></FormField>
            </div>
            {full ? (
              <LineItemsEditor lines={form.lines} onChange={set("lines")} items={items} showStock warehouse={form.warehouse} testid="doc-edit-lines" />
            ) : (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">{t("header_only_note")}</div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="doc-edit-save-btn">{t("save")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
